import { useState, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import type { FilterGroup, FilterGroupItem } from "@/stores/admin/repository";

interface ManageFilterGroupItemsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filterGroup: FilterGroup | null;
  allItems: FilterGroupItem[];
  onItemsSaved: () => void;
}

export const ManageFilterGroupItemsDialog = observer(
  ({
    open,
    onOpenChange,
    filterGroup,
    allItems,
    onItemsSaved,
  }: ManageFilterGroupItemsDialogProps) => {
    const { documentCategoriesStore } = useStores();
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<number>>(new Set());
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [allCategories, setAllCategories] = useState<
      Array<{ id: number; category_name: string; description: string | null }>
    >([]);

    // Load all categories and current selections when dialog opens
    useEffect(() => {
      if (open && filterGroup) {
        setIsLoading(true);
        setSearchQuery("");

        Promise.all([
          documentCategoriesStore.loadFilterGroupItems(filterGroup.id),
          documentCategoriesStore.loadAllCategories(),
        ])
          .then(([items, _]) => {
            // Set selected category IDs from current items
            const itemCategoryIds = new Set(items.map((item) => item.document_category_id));
            setSelectedCategoryIds(itemCategoryIds);
            // Access categories from store instead of repository result
            setAllCategories(
              documentCategoriesStore.categoriesList.map((cat) => ({
                id: cat.id,
                category_name: cat.category_name,
                description: cat.description,
              }))
            );
          })
          .catch((error) => {
            toast.error("Failed to load data");
            console.error(error);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }, [open, filterGroup, documentCategoriesStore]);

    // Build a map of category IDs to their assigned group (excluding current group)
    const categoryToGroupMap = useMemo(() => {
      const map = new Map<number, { groupId: number; groupLabel: string }>();
      for (const item of allItems) {
        if (!filterGroup || item.filter_group_id !== filterGroup.id) {
          const group = documentCategoriesStore.filterGroupsList.find(
            (g) => g.id === item.filter_group_id
          );
          if (group) {
            map.set(item.document_category_id, {
              groupId: group.id,
              groupLabel: group.label,
            });
          }
        }
      }
      return map;
    }, [allItems, filterGroup, documentCategoriesStore.filterGroupsList]);

    // Filter categories based on search query
    const filteredCategories = useMemo(() => {
      if (!searchQuery.trim()) {
        return allCategories;
      }
      const query = searchQuery.toLowerCase();
      return allCategories.filter(
        (cat) =>
          cat.category_name.toLowerCase().includes(query) ||
          (cat.description && cat.description.toLowerCase().includes(query))
      );
    }, [allCategories, searchQuery]);

    // Sort categories: selected first, then alphabetically
    const sortedCategories = useMemo(() => {
      return [...filteredCategories].sort((a, b) => {
        const aSelected = selectedCategoryIds.has(a.id);
        const bSelected = selectedCategoryIds.has(b.id);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return a.category_name.localeCompare(b.category_name);
      });
    }, [filteredCategories, selectedCategoryIds]);

    const handleToggleCategory = (categoryId: number) => {
      setSelectedCategoryIds((prev) => {
        const next = new Set(prev);
        if (next.has(categoryId)) {
          next.delete(categoryId);
        } else {
          next.add(categoryId);
        }
        return next;
      });
    };

    const handleSave = async () => {
      if (!filterGroup) return;

      setIsSaving(true);
      try {
        await documentCategoriesStore.setFilterGroupItems(
          filterGroup.id,
          Array.from(selectedCategoryIds)
        );
        toast.success("Filter group items updated successfully");
        onItemsSaved();
        onOpenChange(false);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to update filter group items");
      } finally {
        setIsSaving(false);
      }
    };

    const handleClose = () => {
      setSelectedCategoryIds(new Set());
      setSearchQuery("");
      onOpenChange(false);
    };

    if (!filterGroup) return null;

    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Manage Items: {filterGroup.label}</DialogTitle>
            <DialogDescription>
              Select which categories should appear under this filter group. Categories can only
              belong to one filter group at a time.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <ScrollArea className="h-[300px] rounded-md border p-2">
              <div className="space-y-1">
                {sortedCategories.map((category) => {
                  const isSelected = selectedCategoryIds.has(category.id);
                  const assignedTo = categoryToGroupMap.get(category.id);
                  const isDisabled = !!assignedTo;

                  return (
                    <div
                      key={category.id}
                      className={`flex items-start space-x-3 rounded-md p-2 hover:bg-muted/50 ${
                        isDisabled ? "opacity-50" : ""
                      }`}
                    >
                      <Checkbox
                        id={`cat-${category.id}`}
                        checked={isSelected}
                        onCheckedChange={() => handleToggleCategory(category.id)}
                        disabled={isDisabled}
                        className="mt-0.5"
                      />
                      <div className="flex-1 space-y-0.5">
                        <label
                          htmlFor={`cat-${category.id}`}
                          className={`text-sm font-medium leading-none ${
                            isDisabled ? "cursor-not-allowed" : "cursor-pointer"
                          }`}
                        >
                          {category.category_name}
                        </label>
                        {category.description && (
                          <p className="text-xs text-muted-foreground">{category.description}</p>
                        )}
                        {assignedTo && (
                          <p className="text-xs text-amber-600">
                            Already in: {assignedTo.groupLabel}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
                {sortedCategories.length === 0 && (
                  <div className="py-4 text-center text-sm text-muted-foreground">
                    {searchQuery.trim()
                      ? "No categories match your search."
                      : "No categories available."}
                  </div>
                )}
              </div>
            </ScrollArea>
          )}

          <div className="text-sm text-muted-foreground">
            {selectedCategoryIds.size} categories selected
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || isLoading}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);
